import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import { Button } from '@01_people/ui-library';
import ScrollMenu from 'react-horizontal-scrolling-menu';
import { useTranslation } from 'react-i18next';
import api from 'utils/api';
import svgCloseCross from '../../../assets/svg/close.svg';
import HeaderSC from './header.styled';
import SvgIcon from '../../svg-icon';
import LeftArrowIcon from '../../../assets/svg/left-arrow.svg';
import RightArrowIcon from '../../../assets/svg/right-arrow.svg';
import MenuItem from './components/menu-item';
import Arrow from './components/arrow';
import routerConfig from '../routes';

const ArrowLeft = (
  <Arrow className="arrow-prev">
    <SvgIcon icon={LeftArrowIcon} />
  </Arrow>
);
const ArrowRight = (
  <Arrow className="arrow-next">
    <SvgIcon icon={RightArrowIcon} />
  </Arrow>
);

export const Menu = list => {
  return list.map(el => {
    const { path, name } = el;
    return <MenuItem key={path} link={path} name={name} />;
  });
};

export function Header({
  responsiveHeaderView,
  responsiveContentView,
  backToAppUrl,
  location,
  appType
}) {
  const { t } = useTranslation('components');
  const logo = useSelector(state => state.ui.logo);
  const enabledModules = useSelector(state => state.ui.modules);
  const mappedMenu = useMemo(
    () =>
      Menu(
        routerConfig.filter(item =>
          enabledModules.find(module => item.module === module)
        )
      ),
    [enabledModules]
  );

  /**
   * Function to get back to the main site.
   * Before redirect, refresh tokens, to have always valid ones
   */
  async function handleBackToAppBtn() {
    try {
      const { accessToken } = await api.refreshAccessToken();
      window.location.replace(`${backToAppUrl}?token=${accessToken}`);
    } catch (error) {
      console.log('error on refresh');
      window.location.replace(localStorage.getItem('websiteUrl'));
    }
  }

  /**
   * Functino to get proper menu based on responsive status and type of device (web, ios, android)
   */
  function getMenuContent() {
    // if we are on web, we want to have choice to return to original site
    if (appType === 'web') {
      // we are not on mobile screen size, so display whole menu
      if (!responsiveContentView) {
        return (
          <>
            <ul className="header-menu">{mappedMenu}</ul>
            {responsiveHeaderView ? (
              <span
                className="responsive-close-btn"
                onClick={handleBackToAppBtn}
                role="presentation"
              >
                <SvgIcon icon={svgCloseCross} alt="close_icon" />
              </span>
            ) : (
              <div className="header-back-wrapper">
                <Button
                  onClick={handleBackToAppBtn}
                  styling="secondary"
                  label={t('header.backButton')}
                />
              </div>
            )}
          </>
        );
      }

      // else display only return button
      return (
        <span
          className="responsive-close-btn"
          onClick={handleBackToAppBtn}
          role="presentation"
        >
          <SvgIcon icon={svgCloseCross} alt="close_icon" />
        </span>
      );
    }

    // else, we are on native app, and not on mobile screen, the app handles return itself
    if (!responsiveContentView) {
      return (
        <>
          <ul className="header-menu">{mappedMenu}</ul>
          <span />
        </>
      );
    }

    // or we dont want to display anything
    return <span />;
  }

  return (
    <HeaderSC>
      <div className="header-outer">
        <div className="header-inner">
          <div className="header-image-wrapper">
            <img src={logo} alt="user_area_logo" />
          </div>
          {getMenuContent()}
        </div>
        {responsiveContentView ? (
          <div className="responsive-subheader">
            <ScrollMenu
              data={mappedMenu}
              scrollToSelected
              alignCenter
              hideSingleArrow
              hideArrows
              selected={location.pathname}
              arrowLeft={ArrowLeft}
              arrowRight={ArrowRight}
            />
          </div>
        ) : null}
      </div>
    </HeaderSC>
  );
}

Header.propTypes = {
  responsiveHeaderView: PropTypes.bool.isRequired,
  responsiveContentView: PropTypes.bool.isRequired,
  backToAppUrl: PropTypes.string.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string
  }).isRequired,
  appType: PropTypes.oneOf(['web', 'androidApp', 'iosApp']).isRequired
};

export default withRouter(Header);
